import {
  addDays,
  endOfDay,
  endOfMonth,
  endOfWeek,
  endOfYear,
  formatDayMonth,
  formatDayMonthYear,
  formatMonthYear,
  isSameDay,
  isSameMonth,
  isSameWeek,
  isSameYear,
  isValidDate,
  MONTHS_SHORT,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
  localOf,
} from '../../utils/localDate';

export type PeriodType = 'all_time' | 'year' | 'month' | 'week' | 'day' | 'custom';

export type NavigablePeriod = 'year' | 'month' | 'week' | 'day';

export interface PeriodSelection {
  type: PeriodType;
  anchor?: string;
}

export interface PeriodBounds {
  min?: Date | null;
  now: Date;
}

export interface ResolvedRange {
  start_date: string;
  end_date: string | null;
}

export const ALL_TIME_FLOOR = localOf(2022, 0, 1);

export const DEFAULT_PERIODS: { label: string; value: PeriodType }[] = [
  { label: 'All time', value: 'all_time' },
  { label: 'Year', value: 'year' },
  { label: 'Month', value: 'month' },
  { label: 'Week', value: 'week' },
  { label: 'Today', value: 'day' },
  { label: 'Custom', value: 'custom' },
];

const NAVIGABLE: readonly PeriodType[] = ['year', 'month', 'week', 'day'];

export const isNavigable = (type: PeriodType): type is NavigablePeriod => NAVIGABLE.includes(type);

const LEGACY_ALIASES: Record<string, PeriodType> = {
  today: 'day',
  yesterday: 'day',
  max: 'all_time',
};

/** Also accepts the values older sessions wrote to localStorage. */
export const normalizePeriodValue = (raw?: string | null): PeriodType | null => {
  if (!raw) return null;
  if (DEFAULT_PERIODS.some(({ value }) => value === raw)) return raw as PeriodType;
  return LEGACY_ALIASES[raw] ?? null;
};

export const periodStart = (type: NavigablePeriod, d: Date): Date => {
  switch (type) {
    case 'day':
      return startOfDay(d);
    case 'week':
      return startOfWeek(d);
    case 'month':
      return startOfMonth(d);
    case 'year':
      return startOfYear(d);
  }
};

export const periodEnd = (type: NavigablePeriod, d: Date): Date => {
  switch (type) {
    case 'day':
      return endOfDay(d);
    case 'week':
      return endOfWeek(d);
    case 'month':
      return endOfMonth(d);
    case 'year':
      return endOfYear(d);
  }
};

export const isCurrentPeriod = (type: NavigablePeriod, d: Date, now: Date): boolean => {
  switch (type) {
    case 'day':
      return isSameDay(d, now);
    case 'week':
      return isSameWeek(d, now);
    case 'month':
      return isSameMonth(d, now);
    case 'year':
      return isSameYear(d, now);
  }
};

/** Compares starts, so the period containing `now` is never future. */
export const isFuturePeriod = (type: NavigablePeriod, d: Date, now: Date): boolean =>
  periodStart(type, d) > periodStart(type, now);

/** Only when the whole period predates the floor, so the floor's own day stays selectable. */
export const isBelowFloor = (type: NavigablePeriod, d: Date, min?: Date | null): boolean =>
  !!min && periodEnd(type, d) < startOfDay(min);

export const isPeriodSelectable = (
  type: NavigablePeriod,
  d: Date,
  { min, now }: PeriodBounds,
): boolean => !isFuturePeriod(type, d, now) && !isBelowFloor(type, d, min);

export const resolveSelection = (
  { type, anchor }: PeriodSelection,
  { now }: PeriodBounds,
): ResolvedRange | null => {
  if (type === 'custom') return null;

  if (type === 'all_time') {
    return { start_date: ALL_TIME_FLOOR.toISOString(), end_date: null };
  }

  const at = anchor ? new Date(anchor) : now;
  if (!isValidDate(at)) return null;

  return {
    start_date: periodStart(type, at).toISOString(),
    end_date: isCurrentPeriod(type, at, now) ? null : periodEnd(type, at).toISOString(),
  };
};

const formatWeekLabel = (at: Date, now: Date): string => {
  const from = startOfWeek(at);
  const to = endOfWeek(at);
  const sameYearAsNow = isSameYear(from, now) && isSameYear(to, now);

  if (!isSameYear(from, to)) {
    return `${formatDayMonthYear(from)} – ${formatDayMonthYear(to)}`;
  }

  const suffix = sameYearAsNow ? '' : ` ${from.getFullYear()}`;

  return isSameMonth(from, to)
    ? `${from.getDate()}–${to.getDate()} ${MONTHS_SHORT[from.getMonth()]}${suffix}`
    : `${formatDayMonth(from)} – ${formatDayMonth(to)}${suffix}`;
};

export const formatPeriodLabel = (
  { type, anchor }: PeriodSelection,
  { now }: PeriodBounds,
): string => {
  if (type === 'all_time') return 'All time';
  if (type === 'custom') return 'Custom';

  const at = anchor ? new Date(anchor) : now;
  if (!isValidDate(at)) return '';

  switch (type) {
    case 'year':
      return String(at.getFullYear());
    case 'month':
      return formatMonthYear(at);
    case 'week':
      return formatWeekLabel(at, now);
    case 'day':
      return isSameYear(at, now) ? formatDayMonth(at) : formatDayMonthYear(at);
  }
};

/** For callers outside React that still pass the old period strings. */
export const resolveLegacyPeriod = (period: string, now: Date): ResolvedRange | null => {
  if (period === 'yesterday') {
    return resolveSelection(
      { type: 'day', anchor: addDays(startOfDay(now), -1).toISOString() },
      { now },
    );
  }

  const type = normalizePeriodValue(period);
  return type ? resolveSelection({ type }, { now }) : null;
};
